import { Card, CardActionArea, CardContent, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

import { isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";
import { homePageHeroColors } from "@/src/theme/tokens";

import {
  HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX,
  HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX,
  VEHICLE_TYPE_ICONS,
  VEHICLE_TYPE_TEST_IDS
} from "@/src/components/molecules/VehicleSelectCard/vehicleSelectCard.constants";

import { VehicleType } from "@/src/types/types";

interface VehicleSelectCardHomeViewProps {
  selectedVehicleType: VehicleType;
  onVehicleTypeChange: (value: VehicleType) => void;
  vehicleLabel: string;
  vehicleType: VehicleType;
}

const CARD_BORDER_WIDTH_PX = 1;

const CARD_TOP_RADIUS = "1.25rem";

const cardLayoutSx = {
  transition: "background-color 0.3s, box-shadow 0.3s",
  width: "100%",
  borderRadius: { xs: `${CARD_TOP_RADIUS} ${CARD_TOP_RADIUS} 0 0` },
  boxSizing: "border-box",
  flex: 1,
  minWidth: 0,
  maxHeight: {
    xs: HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX,
    sm: HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX
  },
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative"
};

const cardActionAreaLayoutSx = {
  height: "100%",
  minWidth: 0,
  display: "flex",
  alignItems: "stretch",
  minHeight: {
    xs: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX}px`,
    sm: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX}px`
  },
  // The card no longer clips its children while the selected border is drawn
  // outside of it, so the ripple and focus highlight are rounded here instead.
  borderRadius: "inherit",
  overflow: "hidden",
  ".MuiCardActionArea-focusHighlight": {
    backgroundColor: "transparent",
    opacity: 0
  }
};

/**
 * Renders the top and side border outside of the card box, so the selected card
 * keeps its full background area instead of giving 1px of it up to the border.
 */
const buildOutsideBorderSx = (borderColor: string) => ({
  border: "none",
  overflow: "visible",
  "&::before": {
    content: '""',
    position: "absolute",
    top: `-${CARD_BORDER_WIDTH_PX}px`,
    left: `-${CARD_BORDER_WIDTH_PX}px`,
    right: `-${CARD_BORDER_WIDTH_PX}px`,
    bottom: 0,
    borderWidth: `${CARD_BORDER_WIDTH_PX}px ${CARD_BORDER_WIDTH_PX}px 0`,
    borderStyle: "solid",
    borderColor,
    borderRadius: `calc(${CARD_TOP_RADIUS} + ${CARD_BORDER_WIDTH_PX}px) calc(${CARD_TOP_RADIUS} + ${CARD_BORDER_WIDTH_PX}px) 0 0`,
    pointerEvents: "none"
  }
});

const buildLightCardSx = (isSelected: boolean, muiTheme: Theme) => ({
  ...cardLayoutSx,
  backgroundColor: isSelected
    ? muiTheme.palette.common.white
    : muiTheme.palette.semantic.secondary.selected,
  ...(isSelected
    ? buildOutsideBorderSx(muiTheme.palette.misc.outlineBorder)
    : { border: "none" }),
  zIndex: isSelected ? 2 : 1
});

const buildDarkCardSx = (isSelected: boolean, muiTheme: Theme) => ({
  ...cardLayoutSx,
  backgroundColor: isSelected
    ? homePageHeroColors.selectorActiveBackground
    : muiTheme.palette.semantic.secondary.hover,
  ...(isSelected
    ? {
        ...buildOutsideBorderSx(muiTheme.palette.misc.outlineBorder),
        mb: `-${CARD_BORDER_WIDTH_PX}px`
      }
    : { border: "none" }),
  zIndex: isSelected ? 2 : 1
});

const buildCardSx = (isSelected: boolean): SxProps<Theme> =>
  schemeSx((muiTheme) =>
    isDarkMode(muiTheme)
      ? buildDarkCardSx(isSelected, muiTheme)
      : buildLightCardSx(isSelected, muiTheme)
  );

const buildLightCardActionAreaSx = (isSelected: boolean, muiTheme: Theme) => ({
  ...cardActionAreaLayoutSx,
  "&.Mui-focusVisible": {
    outline: `2px solid ${
      isSelected ? muiTheme.palette.common.black : muiTheme.palette.common.white
    }`,
    outlineOffset: "-2px"
  },
  "&.Mui-focusVisible .MuiCardActionArea-focusHighlight": {
    backgroundColor: isSelected
      ? muiTheme.palette.misc.outlineBorder
      : muiTheme.palette.action.selected,
    opacity: 1
  }
});

const buildDarkCardActionAreaSx = (isSelected: boolean, muiTheme: Theme) => ({
  ...cardActionAreaLayoutSx,
  "&.Mui-focusVisible": {
    outline: `2px solid ${muiTheme.palette.common.white}`,
    outlineOffset: "-2px"
  },
  "&.Mui-focusVisible .MuiCardActionArea-focusHighlight": {
    backgroundColor: isSelected
      ? muiTheme.palette.misc.outlineBorder
      : muiTheme.palette.action.selected,
    opacity: 1
  }
});

const buildCardActionAreaSx = (isSelected: boolean): SxProps<Theme> =>
  schemeSx((muiTheme) =>
    isDarkMode(muiTheme)
      ? buildDarkCardActionAreaSx(isSelected, muiTheme)
      : buildLightCardActionAreaSx(isSelected, muiTheme)
  );

const cardContentSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  justifyContent: "center",
  alignItems: "center",
  boxSizing: "border-box",
  p: 0,
  px: { xs: 0.5, sm: 1.5 },
  py: { sm: 0 },
  gap: { sm: 1.25 },
  minWidth: 0,
  width: "100%",
  minHeight: {
    xs: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_MOBILE_PX}px`,
    sm: `${HOME_PAGE_VEHICLE_SELECTOR_ROW_HEIGHT_PX}px`
  },
  flex: 1,
  height: "100%",
  "&:last-child": { paddingBottom: 0 }
};

const buildLightIconSx = (isSelected: boolean, muiTheme: Theme) => ({
  width: { xs: 28, sm: 32 },
  height: { xs: 28, sm: 32 },
  fontSize: { xs: 28, sm: 32 },
  color: isSelected
    ? muiTheme.palette.common.black
    : muiTheme.palette.text.secondary,
  flexShrink: 0
});

const buildDarkIconSx = (isSelected: boolean, muiTheme: Theme) => ({
  width: { xs: 28, sm: 32 },
  height: { xs: 28, sm: 32 },
  fontSize: { xs: 28, sm: 32 },
  color: isSelected
    ? muiTheme.palette.common.white
    : muiTheme.palette.text.secondary,
  flexShrink: 0
});

const buildIconSx = (isSelected: boolean): SxProps<Theme> =>
  schemeSx((muiTheme) =>
    isDarkMode(muiTheme)
      ? buildDarkIconSx(isSelected, muiTheme)
      : buildLightIconSx(isSelected, muiTheme)
  );

const buildLightLabelSx = (isSelected: boolean, muiTheme: Theme) => ({
  color: isSelected
    ? muiTheme.palette.common.black
    : muiTheme.palette.text.secondary,
  fontStyle: "normal",
  lineHeight: { xs: 1.2, sm: 1.33 },
  textAlign: "center",
  flexShrink: 1,
  minWidth: 0,
  m: 0,
  maxWidth: "100%",
  overflow: { xs: "hidden", sm: "visible" },
  textOverflow: { xs: "ellipsis", sm: "clip" },
  whiteSpace: { xs: "nowrap", sm: "normal" }
});

const buildDarkLabelSx = (isSelected: boolean, muiTheme: Theme) => ({
  color: isSelected
    ? muiTheme.palette.common.white
    : muiTheme.palette.text.secondary,
  fontStyle: "normal",
  lineHeight: { xs: 1.2, sm: 1.33 },
  textAlign: "center",
  flexShrink: 1,
  minWidth: 0,
  m: 0,
  maxWidth: "100%",
  overflow: { xs: "hidden", sm: "visible" },
  textOverflow: { xs: "ellipsis", sm: "clip" },
  whiteSpace: { xs: "nowrap", sm: "normal" }
});

const buildLabelSx = (isSelected: boolean): SxProps<Theme> =>
  schemeSx((muiTheme) =>
    isDarkMode(muiTheme)
      ? buildDarkLabelSx(isSelected, muiTheme)
      : buildLightLabelSx(isSelected, muiTheme)
  );

const VehicleSelectCardHomeView = (props: VehicleSelectCardHomeViewProps) => {
  const {
    vehicleType,
    selectedVehicleType,
    onVehicleTypeChange,
    vehicleLabel
  } = props;
  const isSelected = selectedVehicleType === vehicleType;
  const VehicleIcon = VEHICLE_TYPE_ICONS[vehicleType];

  return (
    <Card
      raised={isSelected}
      sx={buildCardSx(isSelected)}
      elevation={0}
      data-testid={VEHICLE_TYPE_TEST_IDS[vehicleType]}
    >
      <CardActionArea
        onClick={() => onVehicleTypeChange(vehicleType)}
        sx={buildCardActionAreaSx(isSelected)}
      >
        <CardContent sx={cardContentSx}>
          <VehicleIcon sx={buildIconSx(isSelected)} />
          <Typography variant="h5" sx={buildLabelSx(isSelected)}>
            {vehicleLabel}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default VehicleSelectCardHomeView;
